import * as THREE from 'three';
import { Sky } from 'three/addons/objects/Sky.js';
import { EFFECT_LAYER } from '../enhanced-effects';
import { settingsStore, type GraphicsQuality } from '../../core/settings';

// Sun direction matching the DirectionalLight in scene.ts
export const SUN_POS = new THREE.Vector3(80, 100, 60);
export const SUN_DIR = SUN_POS.clone().normalize();

// Fog / haze color (matches scene FogExp2)
export const HAZE_COLOR = new THREE.Color(0xd4c8a0);

// ---------------------------------------------------------------------------
// Sky mode type
// ---------------------------------------------------------------------------
export type SkyMode = 'procedural' | 'preetham';

// ---------------------------------------------------------------------------
// 1a. Procedural Sky Dome — gradient + sun disc + animated cloud layers
// ---------------------------------------------------------------------------

function createProceduralSky(scene: THREE.Scene): { mesh: THREE.Mesh; update: (dt: number) => void; dispose: () => void } {
  const radius = 500;
  const geo = new THREE.SphereGeometry(radius, 64, 32);

  const uniforms = {
    uTime: { value: 0.0 },
    uSunDir: { value: SUN_DIR.clone() },
    uHazeColor: { value: HAZE_COLOR.clone() },
  };

  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms,
    vertexShader: /* glsl */ `
      varying vec3 vWorldDir;
      void main() {
        vWorldDir = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform vec3 uSunDir;
      uniform vec3 uHazeColor;
      varying vec3 vWorldDir;

      // Simple 2D hash for noise
      float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
      }

      // Value noise
      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        float a = hash(i);
        float b = hash(i + vec2(1.0, 0.0));
        float c = hash(i + vec2(0.0, 1.0));
        float d = hash(i + vec2(1.0, 1.0));
        return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
      }

      // Fractal Brownian Motion
      float fbm(vec2 p) {
        float v = 0.0;
        float a = 0.5;
        for (int i = 0; i < 5; i++) {
          v += a * noise(p);
          p *= 2.0;
          a *= 0.5;
        }
        return v;
      }

      void main() {
        vec3 dir = normalize(vWorldDir);
        float y = dir.y;

        // -- Rich sky gradient with more stops --
        vec3 deepZenith   = vec3(0.18, 0.32, 0.72);  // deep blue overhead
        vec3 zenith       = vec3(0.28, 0.46, 0.78);   // medium blue
        vec3 midSky       = vec3(0.50, 0.66, 0.88);   // pale blue
        vec3 lowerSky     = vec3(0.72, 0.76, 0.82);   // desaturated pale
        vec3 horizonPale  = vec3(0.88, 0.82, 0.72);   // pale warm
        vec3 horizonWarm  = vec3(0.95, 0.78, 0.52);   // warm orange at horizon line
        vec3 twilightBand = vec3(0.98, 0.72, 0.42);   // hot orange twilight band

        vec3 skyColor;
        if (y > 0.0) {
          float t = pow(y, 0.45);
          // Multi-stop gradient: horizon → pale → lower → mid → zenith → deep
          skyColor = mix(horizonWarm, horizonPale, smoothstep(0.0, 0.04, t));
          skyColor = mix(skyColor, lowerSky, smoothstep(0.04, 0.15, t));
          skyColor = mix(skyColor, midSky, smoothstep(0.15, 0.35, t));
          skyColor = mix(skyColor, zenith, smoothstep(0.35, 0.65, t));
          skyColor = mix(skyColor, deepZenith, smoothstep(0.65, 1.0, t));

          // Twilight band: warm glow right at the horizon line
          float twilightFactor = exp(-y * 40.0) * 0.35;
          skyColor = mix(skyColor, twilightBand, twilightFactor);
        } else {
          // Below horizon — haze
          skyColor = mix(horizonWarm, uHazeColor.rgb, pow(clamp(-y, 0.0, 1.0), 0.3));
        }

        // -- Sun disc (larger, more intense) --
        float sunDot = dot(dir, uSunDir);
        // Hard sun disc — slightly larger
        float sunDisc = smoothstep(0.9992, 0.9997, sunDot);
        skyColor += vec3(1.0, 0.98, 0.92) * sunDisc * 4.0;
        // Inner corona — tight bright glow
        skyColor += vec3(1.0, 0.95, 0.85) * pow(max(sunDot, 0.0), 256.0) * 2.5;
        // Outer corona — wider warm glow
        skyColor += vec3(1.0, 0.9, 0.7) * pow(max(sunDot, 0.0), 96.0) * 1.8;
        skyColor += vec3(1.0, 0.85, 0.6) * pow(max(sunDot, 0.0), 16.0) * 0.5;
        // Broad atmospheric scatter around sun
        skyColor += vec3(1.0, 0.8, 0.5) * pow(max(sunDot, 0.0), 4.0) * 0.18;

        // Lens flare streaks — radial lines from sun
        vec3 sunCross = cross(dir, uSunDir);
        float flareAngle = atan(sunCross.y, sunCross.x);
        float flarePulse = pow(max(sunDot, 0.0), 32.0);
        float flareRays = pow(abs(sin(flareAngle * 3.0)), 8.0) + pow(abs(sin(flareAngle * 5.0 + 0.7)), 12.0) * 0.5;
        skyColor += vec3(1.0, 0.92, 0.75) * flareRays * flarePulse * 0.25;

        // -- Cloud layers (only above horizon) --
        if (y > 0.02) {
          // Project onto dome for cloud UVs
          vec2 uv1 = dir.xz / (y + 0.1) * 0.8;
          vec2 uv2 = dir.xz / (y + 0.1) * 1.6;
          vec2 uv3 = dir.xz / (y + 0.1) * 0.4;
          vec2 uv4 = dir.xz / (y + 0.05) * 3.2;   // high-altitude cirrus
          vec2 uv5 = dir.xz / (y + 0.08) * 0.25;   // massive low-frequency shapes

          // Layer 1: large wispy clouds — denser
          float cloud1 = fbm(uv1 + vec2(uTime * 0.008, uTime * 0.003));
          cloud1 = smoothstep(0.38, 0.68, cloud1);

          // Layer 2: medium detail clouds
          float cloud2 = fbm(uv2 + vec2(-uTime * 0.012, uTime * 0.005));
          cloud2 = smoothstep(0.42, 0.72, cloud2);

          // Layer 3: broad subtle cloud shapes
          float cloud3 = fbm(uv3 + vec2(uTime * 0.005, -uTime * 0.002));
          cloud3 = smoothstep(0.45, 0.65, cloud3) * 0.5;

          // Layer 4: high altitude cirrus streaks
          float cirrus = fbm(uv4 + vec2(uTime * 0.015, uTime * 0.002));
          // Stretch cirrus horizontally for streak effect
          float cirrus2 = fbm(vec2(uv4.x * 3.0, uv4.y * 0.4) + vec2(uTime * 0.02, 0.0));
          cirrus = smoothstep(0.48, 0.62, cirrus) * 0.3;
          cirrus += smoothstep(0.52, 0.65, cirrus2) * 0.25;
          // Cirrus only at high elevation
          cirrus *= smoothstep(0.15, 0.45, y);

          // Layer 5: massive low-frequency cloud blanket
          float blanket = fbm(uv5 + vec2(uTime * 0.003, uTime * 0.001));
          blanket = smoothstep(0.50, 0.75, blanket) * 0.25;

          float clouds = min(cloud1 + cloud2 * 0.6 + cloud3 * 0.4 + cirrus + blanket, 1.0);

          // Cloud color: lit from sun side, with dark underside for volume
          vec3 cloudBright = vec3(1.0, 0.97, 0.92);
          vec3 cloudMid    = vec3(0.82, 0.78, 0.72);
          vec3 cloudShadow = vec3(0.55, 0.50, 0.45);   // darker underside

          float cloudLight = pow(max(dot(dir, uSunDir), 0.0), 2.0) * 0.5 + 0.5;
          // Shadow factor: thicker parts get darker undersides
          float thickness = cloud1 * 0.7 + cloud2 * 0.3;
          float shadowFactor = smoothstep(0.3, 0.8, thickness);
          vec3 cloudColor = mix(cloudBright, cloudMid, 1.0 - cloudLight);
          cloudColor = mix(cloudColor, cloudShadow, shadowFactor * 0.5);

          // Fade clouds near horizon
          float horizonFade = smoothstep(0.02, 0.15, y);
          clouds *= horizonFade * 0.7;

          skyColor = mix(skyColor, cloudColor, clouds);
        }

        // -- Atmospheric haze at horizon --
        float hazeFactor = 1.0 - smoothstep(0.0, 0.10, abs(y));
        skyColor = mix(skyColor, uHazeColor.rgb, hazeFactor * 0.45);

        gl_FragColor = vec4(skyColor, 1.0);
      }
    `,
  });

  const mesh = new THREE.Mesh(geo, mat);
  mesh.userData.isEffect = true;
  mesh.layers.set(EFFECT_LAYER); // Remove from layer 0 so raycasts pass through
  mesh.renderOrder = -1000;
  mesh.frustumCulled = false;
  scene.add(mesh);

  return {
    mesh,
    update(dt: number) {
      uniforms.uTime.value += dt;
    },
    dispose() {
      scene.remove(mesh);
      geo.dispose();
      mat.dispose();
    },
  };
}

// ---------------------------------------------------------------------------
// 1b. Preetham Sky — physically-based atmospheric scattering (high/ultra)
// ---------------------------------------------------------------------------

function createPreethamSky(scene: THREE.Scene): { mesh: THREE.Mesh; update: (dt: number) => void; dispose: () => void } {
  const sky = new Sky();
  sky.scale.setScalar(450000);

  const uniforms = sky.material.uniforms;
  uniforms['turbidity'].value = 10;
  uniforms['rayleigh'].value = 2;
  uniforms['mieCoefficient'].value = 0.005;
  uniforms['mieDirectionalG'].value = 0.8;

  // Match sun position to CSM light direction (-0.5, -0.85, -0.35) normalized
  const phi = Math.acos(-(-0.85)); // elevation from sun direction Y
  const theta = Math.atan2(-0.35, -0.5); // azimuth
  const sunPosition = new THREE.Vector3().setFromSphericalCoords(1, phi, theta);
  uniforms['sunPosition'].value.copy(sunPosition);

  // Tag as effect so raycasts ignore it
  sky.userData.isEffect = true;
  sky.layers.set(EFFECT_LAYER);
  sky.renderOrder = -1000;
  sky.frustumCulled = false;
  scene.add(sky);

  return {
    mesh: sky as unknown as THREE.Mesh,
    update(_dt: number) {
      // Preetham sky is static — no per-frame update needed
    },
    dispose() {
      scene.remove(sky);
      sky.geometry.dispose();
      (sky.material as THREE.ShaderMaterial).dispose();
    },
  };
}

// ---------------------------------------------------------------------------
// SkyDome class — supports both procedural and Preetham modes
// ---------------------------------------------------------------------------

export class SkyDome {
  private scene: THREE.Scene;
  private mode: SkyMode;
  private activeSky: { mesh: THREE.Mesh; update: (dt: number) => void; dispose: () => void } | null = null;

  constructor(scene: THREE.Scene, quality?: GraphicsQuality) {
    this.scene = scene;
    this.mode = SkyDome.modeForQuality(quality ?? settingsStore.get().graphicsQuality);
    this.activeSky = this.createForMode(this.mode);
  }

  /** Determine sky mode from quality preset. */
  static modeForQuality(quality: GraphicsQuality): SkyMode {
    return quality === 'high' || quality === 'ultra' ? 'preetham' : 'procedural';
  }

  /** Switch sky mode at runtime. */
  setMode(mode: SkyMode): void {
    if (mode === this.mode && this.activeSky) return;
    this.mode = mode;
    if (this.activeSky) {
      this.activeSky.dispose();
      this.activeSky = null;
    }
    this.activeSky = this.createForMode(mode);
  }

  /** Update sky quality from a quality preset. */
  setQuality(quality: GraphicsQuality): void {
    this.setMode(SkyDome.modeForQuality(quality));
  }

  update(dt: number): void {
    this.activeSky?.update(dt);
  }

  dispose(): void {
    if (this.activeSky) {
      this.activeSky.dispose();
      this.activeSky = null;
    }
  }

  private createForMode(mode: SkyMode): { mesh: THREE.Mesh; update: (dt: number) => void; dispose: () => void } {
    return mode === 'preetham'
      ? createPreethamSky(this.scene)
      : createProceduralSky(this.scene);
  }
}

// ---------------------------------------------------------------------------
// Legacy functional API — kept for backward compatibility
// ---------------------------------------------------------------------------

export function createSkyDome(scene: THREE.Scene): { mesh: THREE.Mesh; update: (dt: number) => void; dispose: () => void } {
  return createProceduralSky(scene);
}
